import { Fragment, useMemo, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import {
    Download,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Search,
    X,
    LogIn,
    Lock
} from "lucide-react";
import PaViewLink from "../../components/ui/PaViewLink";
import ScrollX from "../../components/ui/ScrollX";
import ClientSummaryPanel from "../../components/client-summary/client-summary-panel";
import { formatIsoDate } from "../../utils/dates";
import { formatAdvisors } from "../../utils/advisors";

const PoliciesTable = ({ policies, isRmdBdd, distroId, currentUser, apiUrl }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return policies;
        const q = searchQuery.toLowerCase();
        return policies.filter((p) =>
            p.name?.toLowerCase().includes(q) ||
            p.advisor?.toLowerCase().includes(q)
        );
    }, [policies, searchQuery]);

    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Advisor',
                accessor: 'advisor',
                Cell: ({ value }) => {
                    const advisors = formatAdvisors(value);
                    if (advisors.length === 0) return <span className="text-gray-400">-</span>;
                    return (
                        <div className="flex flex-col gap-1.5 leading-snug">
                            {advisors.map((advisor) => (
                                <span key={advisor} className="text-gray-700 font-medium whitespace-nowrap">
                                    {advisor}
                                </span>
                            ))}
                        </div>
                    );
                },
            },
            {
                Header: 'Company',
                accessor: 'name',
                Cell: ({ row }) => (
                    <span className="text-gray-700 font-medium">
                        {row.original.name}
                        {row.original.enrollmentStatus !== 'Active' && ` (${row.original.enrollmentStatus})`}
                    </span>
                ),
            },
            {
                Header: 'Policy No',
                accessor: 'policyNo',
                Cell: ({ value }) => (
                    <span className="font-medium text-gray-900">{value}</span>
                ),
            },
            {
                Header: 'Employees',
                accessor: 'activeEmployees',
                Cell: ({ value }) => (
                    <span className="font-medium text-gray-900">{value}</span>
                ),
                sortType: 'number',
            },
            {
                Header: 'Activation Date',
                accessor: 'activationDate',
                Cell: ({ value }) => (
                    <span className="text-gray-700">
                        {formatIsoDate(value) || "-"}
                    </span>
                ),
                sortType: (rowA, rowB) => {
                    const dateA = rowA.original.activationDate ? new Date(rowA.original.activationDate) : new Date(0);
                    const dateB = rowB.original.activationDate ? new Date(rowB.original.activationDate) : new Date(0);
                    return dateA - dateB;
                },
            },
        ];

        cols.push({
            Header: 'Actions',
            accessor: 'id',
            disableSortBy: true,
            Cell: ({ row }) => {
                const isExpanded = expandedId === row.original.id;
                return (
                    <div className="inline-flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : row.original.id)}
                            aria-expanded={isExpanded}
                            className="text-primary hover:text-primary-dark text-sm font-medium inline-flex items-center gap-1 transition-colors"
                        >
                            {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                            ) : (
                                <ChevronDown className="w-4 h-4" />
                            )}
                            Client Summary
                        </button>
                        {row.original.isPlanAdmin ? (
                            <>
                                <span className="text-gray-300">|</span>
                                <PaViewLink
                                    policyId={row.original.id}
                                    className="text-emerald-600 hover:text-emerald-700 text-sm font-medium inline-flex items-center gap-1 transition-colors"
                                >
                                    <LogIn className="w-4 h-4" />
                                    Enter as Plan Admin
                                </PaViewLink>
                            </>
                        ) : !isRmdBdd ? (
                            <>
                                <span className="text-gray-300">|</span>
                                <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                                    <Lock className="w-3.5 h-3.5" />
                                    No Plan Admin access
                                </span>
                            </>
                        ) : null}
                    </div>
                );
            },
        });

        return cols;
    }, [isRmdBdd, expandedId]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        rows,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: filteredData,
            initialState: { pageIndex: 0, pageSize: 10 },
            autoResetPage: true,
        },
        useSortBy,
        usePagination
    );

    const downloadCSV = () => {
        const headers = ["Advisor", "Company (Policy)", "Employees", "Activation Date"];
        let csvContent = headers.join(",") + "\n";

        rows.forEach(row => {
            const policy = row.original;
            const advisor = formatAdvisors(policy.advisor).join('; ');
            const name = policy.name || '';
            const employees = policy.activeEmployees || 0;
            const activationDate = formatIsoDate(policy.activationDate);

            csvContent += `"${advisor}","${name}","${employees}","${activationDate}"\n`;
        });

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `Clients and Policies.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getAlignment = (columnId) => {
        if (columnId === 'advisor' || columnId === 'name' || columnId === 'id') return 'text-left';
        return 'text-right';
    };

    // The Actions column (accessor 'id') gets a fixed width so the table layout
    // stays stable regardless of which action(s) render on a given row.
    const getWidthClass = (columnId) => (columnId === 'id' ? 'w-80' : '');

    return (
        <div className="bg-white rounded-2xl shadow-card p-6">
            {/* header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                    Policy List
                </h3>
                <button
                    onClick={downloadCSV}
                    disabled={rows.length === 0}
                    className="h-9 px-4 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 hover:text-gray-900 transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Download CSV"
                >
                    <Download className="w-4 h-4" />
                    <span>CSV</span>
                </button>
            </div>

            {/* Search bar */}
            <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by advisor or company/policy..."
                    className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                />
                {searchQuery && (
                    <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Desktop table */}
            <ScrollX wrapperClassName="hidden md:block">
                <table {...getTableProps()} className="w-full text-sm">
                    <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()} className="border-b border-gray-100">
                                {headerGroup.headers.map(column => (
                                    <th
                                        {...column.getHeaderProps(column.canSort ? column.getSortByToggleProps() : undefined)}
                                        className={`py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider ${getAlignment(column.id)} ${getWidthClass(column.id)} ${
                                            column.canSort ? 'cursor-pointer hover:text-gray-700 transition-colors' : ''
                                        }`}
                                    >
                                        <span className={`inline-flex items-center gap-1 ${
                                            column.id !== 'advisor' && column.id !== 'name' && column.id !== 'id' ? 'justify-end' : ''
                                        }`}>
                                            {column.render('Header')}
                                            {column.isSorted && (
                                                column.isSortedDesc
                                                    ? <ChevronDown className="w-3 h-3" />
                                                    : <ChevronUp className="w-3 h-3" />
                                            )}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()} className="divide-y divide-gray-50">
                        {page.length > 0 ? page.map(row => {
                            prepareRow(row);
                            const isExpanded = expandedId === row.original.id;
                            return (
                                <Fragment key={row.original.id}>
                                    <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                                        {row.cells.map(cell => (
                                            <td
                                                {...cell.getCellProps()}
                                                className={`py-3 px-2 ${getAlignment(cell.column.id)} ${getWidthClass(cell.column.id)}`}
                                            >
                                                {cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                    {isExpanded && (
                                        <tr>
                                            <td colSpan={columns.length} className="p-0">
                                                <ClientSummaryPanel
                                                    summaryUrl={`${apiUrl}/financials/policies/${distroId}/${row.original.id}/summary`}
                                                    expensesUrlFor={(limitId) =>
                                                        `${apiUrl}/financials/policies/${distroId}/${row.original.id}/limits/${limitId}/expenses.pdf`
                                                    }
                                                    token={currentUser.token}
                                                    onCollapse={() => setExpandedId(null)}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        }) : (
                            <tr>
                                <td colSpan={columns.length} className="py-8 text-center text-sm text-gray-400">
                                    No results found for <strong>"{searchQuery}"</strong>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </ScrollX>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
                {page.length > 0 ? page.map(row => {
                    prepareRow(row);
                    const policy = row.original;
                    const advisorList = formatAdvisors(policy.advisor);
                    const activationDate = formatIsoDate(policy.activationDate) || '-';
                    const isExpanded = expandedId === policy.id;

                    return (
                        <div key={policy.id} className="bg-gray-50 rounded-xl p-4 overflow-hidden">
                            <div className="flex justify-between items-start gap-3 mb-2">
                                <div className="min-w-0 flex-1">
                                    <h4 className="font-semibold text-gray-900 truncate">{policy.name}</h4>
                                    <div className="text-sm text-gray-600 flex flex-col gap-1.5 leading-snug">
                                        {advisorList.length > 0
                                            ? advisorList.map((advisor) => (
                                                  <span key={advisor} className="truncate">{advisor}</span>
                                              ))
                                            : "-"}
                                    </div>
                                </div>
                                <div className="flex-shrink-0 text-right">
                                    <div className="text-lg font-bold text-gray-900">{policy.activeEmployees}</div>
                                    <div className="text-xs text-gray-500">employees</div>
                                </div>
                            </div>
                            <div className="text-xs text-gray-500">
                                Activated: {activationDate}
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setExpandedId(isExpanded ? null : policy.id)}
                                    aria-expanded={isExpanded}
                                    className="text-primary hover:text-primary-dark text-sm font-medium inline-flex items-center gap-1 transition-colors"
                                >
                                    {isExpanded ? (
                                        <ChevronUp className="w-4 h-4" />
                                    ) : (
                                        <ChevronDown className="w-4 h-4" />
                                    )}
                                    Client Summary
                                </button>
                                {policy.isPlanAdmin ? (
                                    <PaViewLink
                                        policyId={policy.id}
                                        className="text-emerald-600 hover:text-emerald-700 text-sm font-medium inline-flex items-center gap-1 transition-colors"
                                    >
                                        <LogIn className="w-4 h-4" />
                                        Enter as Plan Admin
                                    </PaViewLink>
                                ) : !isRmdBdd ? (
                                    <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                                        <Lock className="w-3.5 h-3.5" />
                                        No Plan Admin access
                                    </span>
                                ) : null}
                            </div>

                            {isExpanded && (
                                <div className="-mx-4 -mb-4 mt-3">
                                    <ClientSummaryPanel
                                        summaryUrl={`${apiUrl}/financials/policies/${distroId}/${policy.id}/summary`}
                                        expensesUrlFor={(limitId) =>
                                            `${apiUrl}/financials/policies/${distroId}/${policy.id}/limits/${limitId}/expenses.pdf`
                                        }
                                        token={currentUser.token}
                                        onCollapse={() => setExpandedId(null)}
                                    />
                                </div>
                            )}
                        </div>
                    );
                }) : (
                    <div className="py-8 text-center text-sm text-gray-400">
                        No results found for <strong>"{searchQuery}"</strong>
                    </div>
                )}
            </div>

            {/* pagination */}
            {filteredData.length > 10 && (
                <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => gotoPage(0)}
                            disabled={!canPreviousPage}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="First page"
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={previousPage}
                            disabled={!canPreviousPage}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Previous page"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={nextPage}
                            disabled={!canNextPage}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Next page"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => gotoPage(pageOptions.length - 1)}
                            disabled={!canNextPage}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Last page"
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span>
                            Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                        </span>
                        <span className="flex items-center gap-2">
                            Go to:
                            <input
                                type="number"
                                min={1}
                                max={pageOptions.length}
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(pageNumber);
                                }}
                                className="w-14 px-2 py-1 border border-gray-200 rounded-lg text-center focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                            />
                        </span>
                    </div>

                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                    >
                        {[10, 20, 30, 40].map((size) => (
                            <option key={size} value={size}>
                                Show {size}
                            </option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );
};

export default PoliciesTable;